import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import StarIcon from '@mui/icons-material/Star';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TranslateIcon from '@mui/icons-material/Translate';
import WorkIcon from '@mui/icons-material/Work';
import ChatIcon from '@mui/icons-material/Chat';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { PatientReviewItem, ReviewSummary, LocationDirections, EstimatedCosts } from '../../provider';
import SaveToModal from '../../modals/SaveToModal/SaveToModal';
import CreateSavedListModal from '../../modals/CreateSavedListModal/CreateSavedListModal';
import ScheduleAppointmentModal from '../../modals/ScheduleAppointmentModal/ScheduleAppointmentModal';
import { providersApi, ApiError } from '../../../services/api';
import styles from './ProviderDetail.module.css';

interface Provider {
  id: string;
  npi: string;
  username?: string;
  professionalPrefixes?: string[];
  primaryPrefix?: string;
  name: string;
  title: string;
  specialties: string[];
  taxonomyCodes?: {
    code: string;
    name: string;
    isPrimary?: boolean;
  }[];
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  phone: string;
  website?: string;
  acceptingNewPatients: boolean;
  insuranceAccepted: string[];
  languages: string[];
  education: string[];
  experience?: number;
  rating: number;
  reviewCount: number;
  imageUrl?: string;
  bio?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  hospital?: string;
  availability?: {
    date: string;
    times: string[];
  }[];
}

const ProviderDetail: FunctionComponent = () => {
  const { id, username, titles } = useParams();
  const navigate = useNavigate();
  const { isItemSaved } = useSavedItems();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchProvider = async () => {
      setIsLoading(true);
      try {
        let data;
        
        if (username && titles) {
          // Parse titles - could be single (md) or multiple (md-phd-mba)
          const titleList = titles.split('-');
          // Validate that these are valid medical titles
          const validTitles = ['md', 'do', 'dds', 'dr', 'rn', 'np', 'pa', 'dpm', 'phd', 'psyd', 'pharmd', 'mba', 'mph', 'jd', 'ms', 'ma', 'bs', 'ba'];
          const isValidTitle = titleList.every(title => validTitles.includes(title.toLowerCase()));
          
          if (!isValidTitle) {
            navigate('/404');
            return;
          }
          
          data = await providersApi.getByTitleAndUsername(titles, username);
        } else if (id) {
          // Check if id is NPI (10 digits) or regular ID
          const isNPI = /^\d{10}$/.test(id);
          if (isNPI) {
            data = await providersApi.getByNPI(id);
          } else {
            data = await providersApi.getById(id);
          }
        } else {
          navigate('/404');
          return;
        }
        
        setProvider(data);
      } catch (error) {
        console.error('Error fetching provider:', error);
        if (error instanceof ApiError) {
          console.error('API Error:', error.status, error.message);
          if (error.status === 404) {
            navigate('/404');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (id || (username && titles)) {
      fetchProvider();
    }
  }, [id, username, titles, navigate]);

  const handleBookAppointment = () => {
    setShowScheduleModal(true);
  };

  const handleContact = () => {
    const providerId = provider?.npi || id;
    navigate(`/provider/${providerId}/contact`);
  };

  const isSaved = provider ? isItemSaved(provider.id, 'provider') : false;

  const handleSave = () => {
    if (!provider) return;
    setShowSaveModal(true);
  };

  const handleShare = async () => {
    if (navigator.share && provider) {
      try {
        await navigator.share({
          title: provider.name,
          text: `Check out ${provider.name} on Mario Health`,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled the share dialog or an error occurred
        // Silently handle the error - no need to show anything to the user
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (!navigator.share) {
      // Fallback for browsers that don't support native share
      console.log('Web Share API not supported');
      // Could implement a custom share modal here
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading provider details...</p>
      </div>
    );
  }

  if (!provider) {
    return null;
  }

  return (
    <div className={styles.providerDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <div className={styles.headerActions}>
          <button className={styles.actionButton} onClick={handleSave}>
            {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </button>
          <button className={styles.actionButton} onClick={handleShare}>
            <ShareIcon />
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.providerHeader}>
          <div className={styles.providerImage}>
            <img src="/images/provider-placeholder.svg" alt={provider.name} />
          </div>
          <div className={styles.providerInfo}>
            <h1 className={styles.providerName}>{provider.name}</h1>
            <p className={styles.providerTitle}>{provider.title}</p>
            <div className={styles.providerSpecialties}>
              {provider.taxonomyCodes && provider.taxonomyCodes.length > 0 ? (
                provider.taxonomyCodes.map((taxonomy, index) => (
                  <span key={taxonomy.code}>
                    <button 
                      className={styles.specialtyLink}
                      onClick={() => navigate(`/specialty/${taxonomy.code}`)}
                    >
                      {taxonomy.name}
                    </button>
                    {index < provider.taxonomyCodes!.length - 1 && <span className={styles.specialtySeparator}> • </span>}
                  </span>
                ))
              ) : (
                <span>{provider.specialties.join(' • ')}</span>
              )}
            </div>
            <div className={styles.providerRating}>
              <StarIcon className={styles.starIcon} />
              <span className={styles.rating}>{provider.rating}</span>
              <span className={styles.reviewCount}>({provider.reviewCount} reviews)</span>
            </div>
          </div>
        </div>

        <div className={styles.quickInfo}>
          <div className={styles.infoItem}>
            <LocationOnIcon className={styles.infoIcon} />
            <span>
              {provider.address.street}<br />
              {provider.address.city}, {provider.address.state} {provider.address.zip}
            </span>
          </div>
          <div className={styles.infoItem}>
            <PhoneIcon className={styles.infoIcon} />
            <a href={`tel:${provider.phone}`}>{provider.phone}</a>
          </div>
          {provider.website && (
            <div className={styles.infoItem}>
              <LanguageIcon className={styles.infoIcon} />
              <a href={provider.website} target="_blank" rel="noopener noreferrer">
                Website
              </a>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>About</h2>
          <p className={styles.bio}>{provider.bio}</p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Insurance & Pricing</h2>
          <div className={styles.insuranceInfo}>
            {provider.acceptingNewPatients ? (
              <div className={styles.acceptingPatients}>
                <CheckCircleIcon className={styles.checkIcon} />
                <span>Accepting new patients</span>
              </div>
            ) : (
              <div className={styles.notAcceptingPatients}>
                <span>Not accepting new patients</span>
              </div>
            )}
            
            <div className={styles.insurancePlans}>
              <h3 className={styles.plansTitle}>Accepted Insurance Plans</h3>
              <div className={styles.plansList}>
                {provider.insuranceAccepted.map((plan, index) => (
                  <div key={index} className={styles.insurancePlan}>
                    <CheckCircleIcon className={styles.planCheckIcon} />
                    <span>{plan}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Show estimated costs for the first insurance plan */}
            {provider.insuranceAccepted.length > 0 && (
              <EstimatedCosts
                insurancePlan={provider.insuranceAccepted[0]}
                costs={[
                  {
                    label: 'New patient visit',
                    billedAmount: '$450',
                    insurancePays: '$300',
                    patientCost: '$150',
                    savingsPercentage: 25
                  },
                  {
                    label: 'Follow-up visit',
                    billedAmount: '$300',
                    insurancePays: '$200',
                    patientCost: '$100',
                    savingsPercentage: 20
                  },
                  {
                    label: 'Annual physical',
                    billedAmount: '$500',
                    insurancePays: '$500',
                    patientCost: '$0'
                  }
                ]}
              />
            )}
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Credentials & Experience</h2>
          <div className={styles.credentials}>
            <div className={styles.credentialCard}>
              <div className={styles.credentialIcon}>
                <SchoolIcon />
              </div>
              <div className={styles.credentialContent}>
                <h3>Education</h3>
                <ul className={styles.educationList}>
                  {provider.education.map((edu, index) => (
                    <li key={index}>{edu}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            {provider.experience && (
              <div className={styles.credentialCard}>
                <div className={styles.credentialIcon}>
                  <WorkIcon />
                </div>
                <div className={styles.credentialContent}>
                  <h3>Experience</h3>
                  <p className={styles.experienceText}>{provider.experience} years in practice</p>
                </div>
              </div>
            )}
            
            {provider.hospital && (
              <div className={styles.credentialCard}>
                <div className={styles.credentialIcon}>
                  <LocalHospitalIcon />
                </div>
                <div className={styles.credentialContent}>
                  <h3>Hospital Affiliation</h3>
                  <p>{provider.hospital}</p>
                </div>
              </div>
            )}
            
            <div className={styles.credentialCard}>
              <div className={styles.credentialIcon}>
                <TranslateIcon />
              </div>
              <div className={styles.credentialContent}>
                <h3>Languages</h3>
                <p>{provider.languages.join(', ')}</p>
              </div>
            </div>
          </div>
        </div>

        {provider.availability && provider.availability.length > 0 && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Availability</h2>
            <div className={styles.availability}>
              {provider.availability.slice(0, 3).map((slot, index) => (
                <div key={index} className={styles.daySlot}>
                  <h4>{slot.date}</h4>
                  <div className={styles.timeSlots}>
                    {slot.times.slice(0, 3).map((time, timeIndex) => (
                      <button key={timeIndex} className={styles.timeSlot}>
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button className={styles.moreAvailability}>
              <AccessTimeIcon />
              View more times
            </button>
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.reviewsHeader}>
            <h2 className={styles.sectionTitle}>Patient Reviews</h2>
            <button className={styles.writeReviewButton}>
              Write a Review
            </button>
          </div>
          
          <ReviewSummary 
            rating={provider.rating}
            reviewCount={provider.reviewCount}
            distribution={{
              5: Math.floor(provider.reviewCount * 0.7),
              4: Math.floor(provider.reviewCount * 0.2),
              3: Math.floor(provider.reviewCount * 0.06),
              2: Math.floor(provider.reviewCount * 0.03),
              1: Math.floor(provider.reviewCount * 0.01)
            }}
          />
          
          <div className={styles.reviewsList}>
            {/* Sample reviews - would be fetched from API */}
            <PatientReviewItem
              reviewerName="Jane D."
              reviewDate="2 weeks ago"
              rating={5}
              reviewText="Excellent doctor! Very thorough and caring. Takes time to listen to concerns and explain treatment options."
              verified={true}
            />
            <PatientReviewItem
              reviewerName="Michael R."
              reviewDate="1 month ago"
              rating={4}
              reviewText="Great experience overall. The wait time was a bit long but the care was worth it. Dr. Carter is very knowledgeable."
            />
            <PatientReviewItem
              reviewerName="Sarah L."
              reviewDate="2 months ago"
              rating={5}
              reviewText="I've been seeing Dr. Carter for years and she's always been fantastic. Highly recommend!"
              verified={true}
            />
          </div>
          
          <button className={styles.viewAllReviews}>
            View all reviews
          </button>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Location & Directions</h2>
          <LocationDirections
            providerName={provider.name}
            providerAddress={provider.address}
            providerCoordinates={provider.coordinates}
          />
        </div>
      </div>

      <div className={styles.bottomActions}>
        <div className={styles.contactOptions}>
          <button className={styles.contactOptionButton} onClick={handleContact}>
            <ChatIcon />
            <span>Message</span>
          </button>
          <button className={styles.contactOptionButton}>
            <PhoneIcon />
            <span>Call</span>
          </button>
          <button className={styles.contactOptionButton}>
            <VideoCallIcon />
            <span>Video Visit</span>
          </button>
        </div>
        <button className={styles.bookButton} onClick={handleBookAppointment}>
          <CalendarMonthIcon />
          Book Appointment
        </button>
      </div>

      {provider && (
        <SaveToModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          item={{
            id: provider.id,
            type: 'provider',
            title: provider.name,
            subtitle: `${provider.title} • ${provider.specialties[0]}`,
            data: provider
          }}
          onCreateNewList={() => {
            setShowSaveModal(false);
            setShowCreateListModal(true);
          }}
        />
      )}

      <CreateSavedListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onListCreated={(listId) => {
          navigate(`/list/${listId}`);
        }}
      />
      
      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        providerId={provider?.id || 'provider-1'}
        providerName={provider?.name || ''}
        providerImage={`https://api.dicebear.com/6.x/initials/svg?seed=${provider?.name}`}
        service="General Consultation"
      />
    </div>
  );
};

export default ProviderDetail;